/**
 * Authentication Hook
 * Manages user authentication state and provides auth methods
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  role: string;
  isEmailVerified: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  apiKey?: string;
  tenantId?: string;
  webhookSecret?: string;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  loading: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    tokens: null,
    loading: true,
  });

  const navigate = useNavigate();

  // Initialize from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const tokensStr = localStorage.getItem('auth_tokens');
        const userStr = localStorage.getItem('auth_user');

        if (tokensStr && userStr) {
          const tokens = JSON.parse(tokensStr) as AuthTokens;
          const user = JSON.parse(userStr) as User;

          setState({
            user,
            tokens,
            loading: false,
          });
        } else {
          setState({
            user: null,
            tokens: null,
            loading: false,
          });
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setState({
          user: null,
          tokens: null,
          loading: false,
        });
      }
    };

    initializeAuth();
  }, []);

  const setTokens = useCallback((tokens: AuthTokens) => {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    setState(prev => ({ ...prev, tokens }));
  }, []);

  const setUser = useCallback((user: User) => {
    localStorage.setItem('auth_user', JSON.stringify(user));
    setState(prev => ({ ...prev, user }));
  }, []);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('auth_user');
    setState({
      user: null,
      tokens: null,
      loading: false,
    });
  }, []);

  const login = useCallback(async (email: string, password: string, mfaCode?: string): Promise<{ success: boolean; requiresMFA?: boolean; requiresEmailVerification?: boolean; error?: string }> => {
    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, mfaCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Login failed',
        };
      }

      // Backend returns { message, user, tokens } on success
      // OR { message, requiresMFA: true, user } when MFA needed
      if (data.requiresMFA) {
        return {
          success: false,
          requiresMFA: true,
        };
      }

      if (data.requiresEmailVerification) {
        return {
          success: false,
          requiresEmailVerification: true,
        };
      }

      // Successful login - store user and tokens
      if (data.user && data.tokens) {
        const userData: User = data.user;
        const authTokens: AuthTokens = data.tokens;

        setTokens(authTokens);
        setUser(userData);

        return { success: true };
      }

      return { success: false, error: 'Invalid response from server' };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }, [setTokens, setUser]);

  const resetPassword = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Password reset failed',
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Password reset failed:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!state.user) return false;

    const userRole = state.user.role;

    // Define role-based permissions
    const rolePermissions: Record<string, string[]> = {
      'SUPER_ADMIN': ['dashboard', 'system_monitoring', 'backend_latency', 'ai_agent_config', 'bug_monitoring', 'user_management', 'analytics'],
      'ADMIN': ['dashboard', 'analytics', 'user_management'],
      'TENANT': ['dashboard', 'limited_analytics'],
      'USER': ['dashboard']
    };

    return rolePermissions[userRole]?.includes(permission) || false;
  }, [state.user]);

  const hasRole = useCallback((role: string): boolean => {
    return state.user?.role === role;
  }, [state.user]);

  const register = useCallback(async (data: { email: string; password: string; firstName: string; lastName: string; agreeToTerms: boolean }): Promise<{ success: boolean; requiresEmailVerification?: boolean; error?: string }> => {
    try {
      const response = await fetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: responseData.error || 'Registration failed',
        };
      }

      // Backend returns { message, user, tokens } on success
      // OR { message, requiresEmailVerification: true } when email verification needed
      if (responseData.requiresEmailVerification) {
        return {
          success: false,
          requiresEmailVerification: true,
        };
      }

      if (responseData.user && responseData.tokens) {
        const userData: User = responseData.user;
        const authTokens: AuthTokens = responseData.tokens;

        setTokens(authTokens);
        setUser(userData);

        return { success: true };
      }

      return { success: false, error: 'Invalid response from server' };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }, [setTokens, setUser]);

  const logout = useCallback(async (): Promise<void> => {
    if (state.tokens?.refreshToken) {
      try {
        await fetch('/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: state.tokens.refreshToken }),
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    clearAuth();
    navigate('/login');
  }, [state.tokens, clearAuth, navigate]);

  const isAuthenticated = !!state.user;

  return {
    ...state,
    isAuthenticated,
    login,
    register,
    logout,
    resetPassword,
    hasPermission,
    hasRole,
    setTokens,
    setUser,
    clearAuth,
  };
}

export default useAuth;
